import Head from "next/head";

export const STHead = ({
  title = "Sustanix",
  description = "Sustanix, Customized, Toxic-chemical Free, Optimum properties, Products, Microplastic Ingestion, Toxic PFAS Additive, Plastic's Global Impact",
  keywords = "green chemistry, biomass coatings, sustainable additives, toxic-free materials, biodegradable, PFAS free, microplastic free, industrial coatings, circular materials, paper packaging, bio-based coatings, VOCs free, sustainable chemistry",
  canonicalUrl = "https://sustanix.com",
  ogTitle = "Advanced biomaterials using chemistry & biochemistry | Sustainable Biomass Solutions",
  ogDescription = "Sustanix develops novel biomolecules for advanced biomaterials, takes it from lab to industrial scale",
  ogKeywords = "green chemistry, biomass coatings, sustainable additives, toxic-free materials, biodegradable, PFAS free, microplastic free, industrial coatings, circular materials, paper packaging, bio-based coatings, VOCs free, sustainable chemistry",
  ogImage = "https://sustanix.com/opengraph-image.webp",
  url = "https://sustanix.com",
  ogType = "website",
  datePublished,
  dateModified,
}) => {
  const fullTitle = title.includes("Sustanix") ? title : `${title} | Sustanix`;

  let finalOgImage = ogImage || "https://sustanix.com/favicon/web-app-manifest-512x512.png";
  if (finalOgImage && !finalOgImage.startsWith("http")) {
    finalOgImage = finalOgImage.startsWith("/")
      ? `https://sustanix.com${finalOgImage}`
      : `https://sustanix.com/${finalOgImage}`;
  }

  const finalUrl = canonicalUrl || url;

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Sustanix",
    "url": "https://sustanix.com",
    "logo": "https://sustanix.com/favicon/web-app-manifest-512x512.png",
    "description": ogDescription,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Urmonderbaan 22,",
      "addressLocality": "Geleen",
      "addressRegion": "Netherlands",
      "postalCode": "6167RD",
      "addressCountry": "NL"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "findsolution@sustanix.com",
      "telephone": "+31643547865", // Update with your actual phone number
      "availableLanguage": ["English", "Dutch"],
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "17:00"
    },
    "sameAs": [
      "https://www.linkedin.com/company/sustanix/"
      // Add other social links here if needed, e.g., "https://twitter.com/sustanix"
    ]
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": ogType === "article" ? "BlogPosting" : "WebPage",
    "name": fullTitle,
    "description": description,
    "url": finalUrl,
    "image": finalOgImage,
    "publisher": {
      "@type": "Organization",
      "name": "Sustanix",
      "logo": {
        "@type": "ImageObject",
        "url": "https://sustanix.com/favicon/web-app-manifest-512x512.png"
      }
    }
  };

  if (ogType === "article") {
    pageSchema.headline = fullTitle;
    pageSchema.author = {
      "@type": "Organization",
      "name": "Sustanix"
    };
    if (datePublished) pageSchema.datePublished = datePublished;
    if (dateModified) pageSchema.dateModified = dateModified;
  }

  const schemaArray = [orgSchema, pageSchema];

  return (
    <Head>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link
        rel="icon"
        type="image/png"
        href="/favicon/favicon-96x96.png"
        sizes="96x96"
      />
      <link rel="icon" type="image/svg+xml" href="/favicon/favicon.svg" />
      <link rel="shortcut icon" href="/favicon/favicon.ico" />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/favicon/apple-touch-icon.png"
      />
      <link rel="manifest" href="/favicon/site.webmanifest" />
      <title>{fullTitle}</title>

      <meta
        name="description"
        key="description"
        content={description}
      />
      <meta
        name="keywords"
        key="keywords"
        content={keywords}
      />
      <meta name="author" key="author" content="Sustanix" />
      <meta name="robots" key="robots" content="index, follow" />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" key="canonical" href={canonicalUrl} />}

      {/* Open Graph Tags */}
      <meta property="og:type" key="og:type" content={ogType} />
      <meta
        property="og:title"
        key="og:title"
        content={ogTitle}
      />
      <meta
        property="og:description"
        key="og:description"
        content={ogDescription}
      />
      <meta property="og:url" key="og:url" content={finalUrl} />
      <meta property="og:image" key="og:image" content={encodeURI(finalOgImage)} />
      <meta property="og:image:secure_url" key="og:image:secure_url" content={encodeURI(finalOgImage)} />
      <meta property="og:site_name" key="og:site_name" content="Sustanix" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" key="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        key="twitter:title"
        content={ogTitle}
      />
      <meta
        name="twitter:description"
        key="twitter:description"
        content={ogDescription}
      />
      <meta name="twitter:image" key="twitter:image" content={encodeURI(finalOgImage)} />
      <meta name="twitter:site" key="twitter:site" content="@Sustanix" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schemaArray),
        }}
      />
    </Head>
  );
};
